import { getApiErrorMessage } from '@moko/utils';
import { fromApiDateOnly, toApiDateOnly } from '../../utils/dateOnly';
import type {
  IPricingItem,
  ITenantPricingRate,
  ITenantPricingRateCreateBody,
  ITenantPricingRateUpdateBody,
} from '../../services/pricingService';

export const pricingItemLabel = (item: IPricingItem) =>
  item.name ? `${item.name} (${item.code})` : item.code;

export type TenantPricingOverrideFormValues = {
  pricingItemId: string;
  currencyCode: string;
  amount: string;
  effectiveFrom: string;
  effectiveTo: string;
};

export type TenantPricingOverrideStatus = 'Scheduled' | 'Active' | 'Expired';

export const emptyOverrideForm: TenantPricingOverrideFormValues = {
  pricingItemId: '',
  currencyCode: '',
  amount: '',
  effectiveFrom: '',
  effectiveTo: '',
};

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

export const validateOverrideAmount = (value: string): string | true => {
  const trimmed = value.trim();
  if (!trimmed) return 'Amount is required.';
  if (!AMOUNT_PATTERN.test(trimmed))
    return 'Amount must be a number with at most two decimal places.';
  if (Number(trimmed) <= 0) return 'Amount must be greater than 0.';
  return true;
};

export const validateOverrideEffectiveTo = (
  effectiveTo: string,
  effectiveFrom: string
): string | true => {
  const to = fromApiDateOnly(effectiveTo);
  const from = fromApiDateOnly(effectiveFrom);
  if (to && from && to < from)
    return 'Effective to must be on or after effective from.';
  return true;
};

// Inclusive date-only range; a missing end date is open-ended.
export const getOverrideStatus = (
  effectiveFrom: string,
  effectiveTo: string | null | undefined,
  today = toApiDateOnly(new Date())
): TenantPricingOverrideStatus => {
  const from = fromApiDateOnly(effectiveFrom);
  const to = fromApiDateOnly(effectiveTo);
  if (from && from > today) return 'Scheduled';
  if (to && to < today) return 'Expired';
  return 'Active';
};

export const toOverrideFormValues = (
  rate: ITenantPricingRate
): TenantPricingOverrideFormValues => ({
  pricingItemId: String(rate.pricingItemId),
  currencyCode: rate.currencyCode,
  amount: Number(rate.amount).toFixed(2),
  effectiveFrom: fromApiDateOnly(rate.effectiveFrom),
  effectiveTo: fromApiDateOnly(rate.effectiveTo),
});

export const toOverrideCreateBody = (
  values: TenantPricingOverrideFormValues
): ITenantPricingRateCreateBody => ({
  pricingItemId: Number(values.pricingItemId),
  currencyCode: values.currencyCode,
  amount: Number(values.amount.trim()),
  effectiveFrom: toApiDateOnly(values.effectiveFrom),
  effectiveTo: toApiDateOnly(values.effectiveTo) || null,
});

// PUT keeps omitted fields, so only changed fields are sent; null clears effectiveTo.
export const toOverrideUpdateBody = (
  original: ITenantPricingRate,
  values: TenantPricingOverrideFormValues
): ITenantPricingRateUpdateBody => {
  const next = toOverrideCreateBody(values);
  const body: ITenantPricingRateUpdateBody = {};
  if (next.pricingItemId !== original.pricingItemId)
    body.pricingItemId = next.pricingItemId;
  if (next.currencyCode !== original.currencyCode)
    body.currencyCode = next.currencyCode;
  if (next.amount !== Number(original.amount)) body.amount = next.amount;
  if (next.effectiveFrom !== fromApiDateOnly(original.effectiveFrom))
    body.effectiveFrom = next.effectiveFrom;
  if (next.effectiveTo !== (fromApiDateOnly(original.effectiveTo) || null))
    body.effectiveTo = next.effectiveTo;
  return body;
};

const API_STATUS = /API Error: (\d{3})/;

export const getOverrideErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const raw =
    error instanceof Error
      ? error.message
      : String((error as { message?: unknown })?.message ?? '');
  const status = Number(API_STATUS.exec(raw)?.[1]);
  const detail = getApiErrorMessage(error, fallback);
  if (status === 409)
    return 'This override overlaps another override for the same pricing item and currency. Effective dates are inclusive.';
  if (status === 404)
    return `Not found: ${detail}. The override, pricing item, or currency may no longer exist.`;
  if (status === 400) return `Invalid override: ${detail}`;
  return detail;
};
